import { useContext, useEffect, useRef } from 'react';
import UnsavedChangesContext from '../Context/UnsavedChangesContext';

// Reports to the UnsavedChangesProvider that this page has unsaved changes while `dirty` is true,
// so leaving the page asks for confirmation. Call allowNavigation() right before navigating away
// on purpose (after a successful save or delete), otherwise that navigation is held back too.
function useUnsavedChanges(dirty) {
  const { setDirty } = useContext(UnsavedChangesContext);

  useEffect(() => {
    setDirty(dirty);
  }, [dirty, setDirty]);

  useEffect(() => () => setDirty(false), [setDirty]);

  return { allowNavigation: () => setDirty(false) };
}

// For forms with no dirty flag of their own: `value` (the editable state, kept small) is
// compared against what it was on the first render where `ready` is true, i.e. once the form
// has been filled from the server. While `ready` is false nothing counts as a change.
function useUnsavedChangesGuard(value, ready = true) {
  const serialized = JSON.stringify(value);
  const baselineRef = useRef(null);

  if (!ready) {
    baselineRef.current = null;
  } else if (baselineRef.current === null) {
    baselineRef.current = serialized;
  }

  return useUnsavedChanges(ready && serialized !== baselineRef.current);
}

export { useUnsavedChanges, useUnsavedChangesGuard };
